import type { CSSProperties, KeyboardEvent } from 'react';
import type { GiantId } from '../../../../../domain/ids/ids';
import {
  DELIVERY_ITEM_ASSET_COUNT,
  getDeliveryItemAssetIndex,
} from '../../../../../domain/catalog/deliveryItemAssets';
import './DeliveryItemImage.css';
import { getDeliveryItemCardLayout } from './deliveryItemCardLayouts';
import { getDeliveryItemDialogLayout } from './deliveryItemLayouts';
import { DELIVERY_ITEM_IMAGES } from './deliveryItemImages';

export type DeliveryItemImagePlacement =
  | 'success-delivery'
  | 'new-delivery'
  | 'delivery-card';

export type DeliveryItemImageProps = {
  giantId: GiantId;
  orderIndex: number;
  placement: DeliveryItemImagePlacement;
  className?: string;
  /** When true, all arts stay mounted but invisible (avoids remount flicker). */
  hidden?: boolean;
  /** Click / keyboard activate (e.g. vinyl music toggle). Enables pointer events. */
  onActivate?: () => void;
};

function handleActivateKeyDown(
  event: KeyboardEvent<HTMLImageElement>,
  onActivate: () => void,
): void {
  if (event.key !== 'Enter' && event.key !== ' ') {
    return;
  }
  event.preventDefault();
  onActivate();
}

const PLACEMENT_CLASS: Record<DeliveryItemImagePlacement, string> = {
  'success-delivery': 'delivery-item-image--success-delivery',
  'new-delivery': 'delivery-item-image--new-delivery',
  'delivery-card': 'delivery-item-image--delivery-card',
};

const DIALOG_PLACEMENTS: ReadonlySet<DeliveryItemImagePlacement> = new Set([
  'success-delivery',
  'new-delivery',
]);

const DELIVERY_ITEM_ASSET_INDICES = Array.from(
  { length: DELIVERY_ITEM_ASSET_COUNT },
  (_, index) => index + 1,
);

/** Card placement keeps every item art mounted to avoid src-swap flicker while paging. */
function DeliveryCardItemImages({
  assetIndex,
  className,
  hidden = false,
  onActivate,
}: {
  assetIndex: number;
  className?: string;
  hidden?: boolean;
  onActivate?: () => void;
}) {
  return (
    <>
      {DELIVERY_ITEM_ASSET_INDICES.map((index) => {
        const layout = getDeliveryItemCardLayout(index);
        const src = DELIVERY_ITEM_IMAGES[index];
        if (layout == null || src == null) {
          return null;
        }

        const isSelected = !hidden && index === assetIndex;
        const interactive = isSelected && onActivate != null;
        return (
          <img
            key={index}
            className={[
              'delivery-item-image',
              PLACEMENT_CLASS['delivery-card'],
              isSelected ? null : 'delivery-item-image--hidden',
              interactive ? 'delivery-item-image--interactive' : null,
              className,
            ]
              .filter(Boolean)
              .join(' ')}
            style={{
              left: layout.left,
              top: layout.top,
              width: layout.width,
              height: layout.height,
            }}
            src={src}
            alt=""
            draggable={false}
            aria-hidden={!isSelected}
            role={interactive ? 'button' : undefined}
            tabIndex={interactive ? 0 : undefined}
            onClick={interactive ? onActivate : undefined}
            onKeyDown={
              interactive
                ? (event) => handleActivateKeyDown(event, onActivate)
                : undefined
            }
          />
        );
      })}
    </>
  );
}

export function DeliveryItemImage({
  giantId,
  orderIndex,
  placement,
  className,
  hidden = false,
  onActivate,
}: DeliveryItemImageProps) {
  const assetIndex = getDeliveryItemAssetIndex({ giantId, orderIndex });
  if (assetIndex == null) {
    return null;
  }

  const src = DELIVERY_ITEM_IMAGES[assetIndex];
  if (src == null) {
    return null;
  }

  if (placement === 'delivery-card') {
    return (
      <DeliveryCardItemImages
        assetIndex={assetIndex}
        className={className}
        hidden={hidden}
        onActivate={onActivate}
      />
    );
  }

  let style: CSSProperties | undefined;
  if (DIALOG_PLACEMENTS.has(placement)) {
    const dialogLayout = getDeliveryItemDialogLayout(assetIndex);
    if (dialogLayout == null) {
      return null;
    }
    style = {
      left: dialogLayout.left,
      top: dialogLayout.top,
      width: dialogLayout.width,
      height: dialogLayout.height,
    };
  }

  const interactive = onActivate != null;

  return (
    <img
      className={[
        'delivery-item-image',
        PLACEMENT_CLASS[placement],
        interactive ? 'delivery-item-image--interactive' : null,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      style={style}
      src={src}
      alt=""
      draggable={false}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? onActivate : undefined}
      onKeyDown={
        interactive
          ? (event) => handleActivateKeyDown(event, onActivate)
          : undefined
      }
    />
  );
}
